import { format, isValid } from "date-fns";
import { ObjectId } from "mongodb";

import { SurveyResultsOverviewResponse } from "@performyard/models";
import {
	CopySurveyRequestJson,
	CreateSurveyRequest,
	CreateSurveyResponseRequestJson,
	GenericSuccessResponse,
	GetAutoflowByIdResponseJson,
	GetSurveyLinkResponseJson,
	ReScheduleSurveyEmailRequestJson,
	ScheduleSurveyEmailRequestJson,
	SurveyModelJson,
	SurveyParticipantJson,
	SurveyQuestionsResultsResponse,
	SurveyResultsCSVResponse,
	SurveyResultsRequestJson,
	UpdateSurveyRequestJson,
} from "@performyard/service-contracts";

import request, { internalRequest } from "./internal-base-request";

export const createSurvey = async (data: CreateSurveyRequest) => {
	const [responseData] = await request<SurveyModelJson>(`/v1/surveys`, {
		method: "POST",
		body: JSON.stringify({
			...data,
		}),
	});
	return responseData;
};

export const copySurvey = async (data: CopySurveyRequestJson) => {
	const [responseData] = await request<SurveyModelJson>(`/v1/surveys/copy`, {
		method: "POST",
		body: JSON.stringify(data),
	});
	return responseData;
};

export const deleteSurvey = async (surveyId: string) => {
	const [responseData] = await request<GenericSuccessResponse>(`/v1/surveys/${surveyId}`, {
		method: "DELETE",
		body: JSON.stringify({}),
	});
	return responseData;
};

export const getSurvey = async (surveyId: string): Promise<SurveyModelJson> => {
	const [data] = await request<SurveyModelJson>(`/v1/surveys/${surveyId}`);
	return data;
};

export const updateSurvey = async (
	surveyId: string,
	data: Omit<UpdateSurveyRequestJson, "startDate" | "endDate"> & { startDate?: Date; endDate?: Date | null }
) => {
	const startDate = data.startDate && isValid(data.startDate) ? format(data.startDate, "yyyy-MM-dd") : undefined;
	const endDate =
		data.endDate === null
			? null
			: data.endDate && isValid(data.endDate)
				? format(data.endDate, "yyyy-MM-dd")
				: undefined;
	const body: UpdateSurveyRequestJson = {
		...data,
		startDate,
		endDate,
	};
	const [responseData] = await request<SurveyModelJson>(`/v1/surveys/${surveyId}`, {
		method: "PATCH",
		body: JSON.stringify(body),
	});
	return responseData;
};

export const getParticipantsEligibleForEmail = async (surveyId: string): Promise<SurveyParticipantJson[]> => {
	const [data] = await request<SurveyParticipantJson[]>(`/v1/surveys/participants-eligible-for-email/${surveyId}`);
	return data;
};

export const getSurveyRespondents = async (surveyId: string): Promise<ObjectId[]> => {
	const [data] = await request<ObjectId[]>(`/v1/surveys/respondents/${surveyId}`);
	return data;
};

export const getSurveyResultsOverview = async (surveyId: string): Promise<SurveyResultsOverviewResponse> => {
	const [data] = await request<SurveyResultsOverviewResponse>(`/v1/surveys/results/overview/${surveyId}`);
	return data;
};

export const getSurveyQuestionsResults = async (
	surveyId: string,
	requestData: SurveyResultsRequestJson
): Promise<SurveyQuestionsResultsResponse> => {
	const [data] = await request<SurveyQuestionsResultsResponse>(`/v1/surveys/results/questions/${surveyId}`, {
		method: "POST", // using POST to allow passing a JSON body with the request data
		body: JSON.stringify(requestData),
	});
	return data;
};

export const downloadSurveyResultsCSV = async (
	surveyId: string,
	data: SurveyResultsRequestJson
): Promise<SurveyResultsCSVResponse> => {
	const [responseData] = await request<SurveyResultsCSVResponse>(`/v1/surveys/results/csv/${surveyId}`, {
		method: "POST",
		body: JSON.stringify(data),
	});
	return responseData;
};

export const getSurveyByLinkId = async (surveyLinkId: string): Promise<GetSurveyLinkResponseJson> => {
	const [data] = await request<GetSurveyLinkResponseJson>(`/v1/survey/${surveyLinkId}`);
	return data;
};

export const createSurveyResponse = async (surveyId: string, data: CreateSurveyResponseRequestJson) => {
	const [responseData] = await request<{ success: boolean; message?: string }>(`/v1/survey/${surveyId}/response`, {
		method: "POST",
		body: JSON.stringify(data),
	});
	return responseData;
};

export const getSurveyScheduleEmailEventsByResourceId = async (resourceId: string) => {
	const [data] = await internalRequest<{
		autoflow: GetAutoflowByIdResponseJson;
		scheduledEvents: Record<string, unknown>;
	}>(`/v1/surveys/schedule-email/${resourceId}`, {
		method: "GET",
	});
	return data;
};

export const reScheduleSurveyEmail = async (data: ReScheduleSurveyEmailRequestJson) => {
	const [responseData] = await request<{ success: boolean; message?: string }>(
		`/v1/surveys/schedule-email/${data.surveyId}`,
		{
			method: "PATCH",
		}
	);
	return responseData;
};

export const scheduleSurveyEmail = async (data: ScheduleSurveyEmailRequestJson) => {
	const [responseData] = await request<{ success: boolean; message?: string }>(`/v1/surveys/schedule-email`, {
		method: "POST",
		body: JSON.stringify(data),
	});
	return responseData;
};
